import { useEffect, useState, useCallback } from "react";
import { cable } from "@/utils/cable";

export interface OutfitImageGenerationStatus {
  outfit_id: string;
  status: 'generating' | 'downloading' | 'completed' | 'error';
  message: string;
  image_url?: string;
  timestamp: string;
}

export function useOutfitImageGeneration(outfitId: string | null) {
  const [status, setStatus] = useState<OutfitImageGenerationStatus | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleStatusUpdate = useCallback((data: OutfitImageGenerationStatus) => {
    setStatus(data);
    // Set generating state for any active generation status
    const isActiveGeneration = data.status === 'generating' || data.status === 'downloading';
    setIsGenerating(isActiveGeneration);
  }, []);

  const startGeneration = useCallback((initialOutfitId: string) => {
    setStatus({
      outfit_id: initialOutfitId,
      status: 'generating',
      message: 'Starting AI image generation...',
      timestamp: new Date().toISOString()
    });
    setIsGenerating(true);
  }, []);

  useEffect(() => {
    if (!outfitId) return;
    
    const subscription = cable.subscriptions.create(
      { 
        channel: "OutfitImageGenerationChannel", 
        outfit_id: outfitId 
      },
      {
        received: (data: OutfitImageGenerationStatus) => {
          handleStatusUpdate(data);
        },
        connected: () => {},
        disconnected: () => {},
        rejected: () => {}
      }
    );

    return () => {
      cable.subscriptions.remove(subscription);
    };
  }, [outfitId, handleStatusUpdate]);

  return {
    status,
    isGenerating,
    startGeneration
  };
}
